import { Component, Input } from '@angular/core';
import { CreateInventoryModal } from '@inventory/constants/inventory-constants';
import { IonNav, ModalController, ViewWillEnter } from '@ionic/angular';
import { PlatformService } from '@panamax/app-state';
import { CreateInventoryInfo, SubCategory } from '@usf/inventory-types';
import { CreateInventoryService } from '@inventory/pages/create-inventory/services/create-inventory.service';
import { CreateInventoryModalValues } from '../../../../../models/create-inventory.model';
import { Observable } from 'rxjs';
import { ListsViewModel } from '../../../../../../lists/model/lists-view.model';
import { createListKey } from '../../../../../../lists/shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { InventoryViewModel } from '../../../../../models/inventory-view.model';
import { ListConstants } from '@usf/ngrx-list';
import { ListTypes } from '@shared/constants/lists-constants';

@Component({
  template: '',
})
export class PopulateInventoryBaseComponent {
  readonly CreateInventoryModal = CreateInventoryModal;

  @Input() selectedCreateInventoryOption: SubCategory;
  @Input() inventories: Observable<InventoryViewModel[]>;
  @Input() shoppingLists: Observable<ListsViewModel[]>;
  @Input() validTimes: Observable<boolean[]>;
  @Input() inventoryConstants: Observable<CreateInventoryInfo>;

  title = 'i18n.inventory.createInventoryPopulate';
  nextButtonEnabled: boolean;
  dataToReturn: CreateInventoryModalValues;

  constructor(
    protected nav: IonNav,
    private modalController: ModalController,
    readonly platformService: PlatformService,
    protected createInventoryService: CreateInventoryService,
  ) {}

  async dismissModal() {
    if (this.dataToReturn?.selectedInventory) {
      this.deselectInventory();
    }
    if (this.dataToReturn?.selectedShoppingList) {
      this.deselectShoppingList();
    }
    await this.modalController.dismiss();
  }

  async onClickNext() {
    if (
      this.selectedCreateInventoryOption?.code === CreateInventoryModal.manual
    ) {
      this.dataToReturn = {
        selectedCreateInventoryOption: this.selectedCreateInventoryOption,
      };
    }
    await this.modalController.dismiss(this.dataToReturn);
  }

  onSelectInventory(selectedInventory: InventoryViewModel) {
    const newIsSelectedValue = !selectedInventory.isSelected;
    this.createInventoryService.updateSelectedInventory(
      selectedInventory.inventoryId,
      newIsSelectedValue,
    );

    this.nextButtonEnabled = newIsSelectedValue ? true : false;
    this.dataToReturn = {
      selectedCreateInventoryOption: this.selectedCreateInventoryOption,
      selectedInventory: this.nextButtonEnabled ? selectedInventory : undefined,
    };
  }

  onSearchInventories(searchKey: string) {
    this.inventories = this.createInventoryService.getInventories$(searchKey);
  }

  onSelectShoppingList(selectedShoppingList: ListsViewModel) {
    const newIsSelectedValue = !selectedShoppingList.isSelected;
    if (selectedShoppingList.listName === ListTypes.recentlyPurchased) {
      this.createInventoryService.onSelectRecentPurchase(newIsSelectedValue);
    } else if (
      selectedShoppingList.listKey.listTypeId === 'ML' ||
      selectedShoppingList.listKey.listTypeId === 'R-ML'
    ) {
      this.createInventoryService.onSelectMasterList(
        selectedShoppingList.listKey.listId,
        newIsSelectedValue,
      );
    } else if (
      selectedShoppingList.listKey.listTypeId ===
      ListConstants.orderGuideListType
    ) {
      this.createInventoryService.onSelectOrderGuide(
        createListKey(selectedShoppingList.listKey),
        newIsSelectedValue,
      );
    } else {
      this.createInventoryService.onSelectShoppingList(
        createListKey(selectedShoppingList.listKey),
        newIsSelectedValue,
      );
    }

    this.nextButtonEnabled = newIsSelectedValue ? true : false;
    this.dataToReturn = {
      selectedCreateInventoryOption: this.selectedCreateInventoryOption,
      selectedShoppingList: this.nextButtonEnabled
        ? selectedShoppingList
        : undefined,
    };
  }

  onSearchShoppingLists(searchKey: string) {
    this.shoppingLists = this.createInventoryService.getShoppingLists$(
      searchKey,
    );
  }

  onPastPurchaseSelection(data: any) {
    this.nextButtonEnabled = true;
    if (
      this.selectedCreateInventoryOption?.code ===
      CreateInventoryModal.pastPurchase
    ) {
      this.dataToReturn = {
        selectedCreateInventoryOption: this.selectedCreateInventoryOption,
        pastPurchaseDays: data,
      };
    }
  }

  deselectInventory() {
    this.createInventoryService.updateSelectedInventory(
      this.dataToReturn.selectedInventory.inventoryId,
      false,
    );
    this.dataToReturn.selectedInventory = undefined;
  }

  deselectShoppingList() {
    if (
      this.dataToReturn.selectedShoppingList.listName ===
      ListTypes.recentlyPurchased
    ) {
      this.createInventoryService.onSelectRecentPurchase(false);
    } else if (
      this.dataToReturn.selectedShoppingList.listKey.listTypeId === 'ML' ||
      this.dataToReturn.selectedShoppingList.listKey.listTypeId === 'R-ML'
    ) {
      this.createInventoryService.onSelectMasterList(
        this.dataToReturn.selectedShoppingList.listKey.listId,
        false,
      );
    } else if (
      this.dataToReturn.selectedShoppingList.listKey.listTypeId ===
      ListConstants.orderGuideListType
    ) {
      this.createInventoryService.onSelectOrderGuide(
        createListKey(this.dataToReturn.selectedShoppingList.listKey),
        false,
      );
    } else {
      this.createInventoryService.onSelectShoppingList(
        createListKey(this.dataToReturn.selectedShoppingList.listKey),
        false,
      );
    }
    this.dataToReturn.selectedShoppingList = undefined;
  }

  setNextButtonEnabled(selectedCreateInventoryOptionCode: string) {
    switch (selectedCreateInventoryOptionCode) {
      case CreateInventoryModal.populateInventory.valueOf():
        return false;
      case CreateInventoryModal.pastInventory.valueOf():
        return this.dataToReturn?.selectedInventory ? true : false;
      case CreateInventoryModal.shoppingList.valueOf():
        return this.dataToReturn?.selectedShoppingList ? true : false;
      case CreateInventoryModal.pastPurchase.valueOf():
        return this.dataToReturn?.pastPurchaseDays ? true : false;
      case CreateInventoryModal.manual.valueOf():
        return true;
      default:
        return false;
    }
  }
}
